import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Badge,
  Button,
  IconButton,
  InputBase,
  Tooltip,
  Typography,
} from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import SendIcon from "@mui/icons-material/Send";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ImageOutlinedIcon from "@mui/icons-material/ImageOutlined";
import VideocamOutlinedIcon from "@mui/icons-material/VideocamOutlined";
import DescriptionOutlinedIcon from "@mui/icons-material/DescriptionOutlined";
import StickyNote2OutlinedIcon from "@mui/icons-material/StickyNote2Outlined";
import { makeStyles } from 'tss-react/mui';
import { toast } from "../../services/toast";

import api from "../../services/api";
import toastError from "../../errors/toastError";
import useTemplateMessage from "../../hooks/useTemplateMessage";
import { AuthContext } from "../../context/Auth/AuthContext";

const AUTO_SOURCES = ["CONTACT_NAME", "USER_NAME"];

const MEDIA_HEADER_FORMATS = ["IMAGE", "VIDEO", "DOCUMENT"];

const ACCEPT_BY_HEADER_FORMAT = {
  IMAGE: "image/*",
  VIDEO: "video/*",
  DOCUMENT:
    ".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx,application/pdf,application/msword,application/vnd.openxmlformats-officedocument.wordprocessingml.document",
};

const HEADER_FORMAT_LABEL = {
  IMAGE: "una imagen",
  VIDEO: "un video",
  DOCUMENT: "un documento",
};

const MEDIA_TYPE_LABEL = {
  IMAGE: "Imagen",
  VIDEO: "Video",
  DOCUMENT: "Documento",
};

const MEDIA_TYPE_ICON = {
  IMAGE: ImageOutlinedIcon,
  VIDEO: VideocamOutlinedIcon,
  DOCUMENT: DescriptionOutlinedIcon,
};

const WINDOW_EXPLANATION =
  "Pasó el tiempo para responderle por chat (o el cliente aún no ha escrito). Para escribirle, envíale una plantilla aprobada y espera a que responda.";

const prettifyTemplateName = (name = "") =>
  name
    .replace(/[_-]+/g, " ")
    .replace(/\s+/g, " ")
    .trim()
    .replace(/^\w/, (c) => c.toUpperCase());

const useStyles = makeStyles()((theme) => ({
  wrapper: {
    flex: 1,
    width: "100%",
    minWidth: 0,
    display: "flex",
    flexDirection: "column",
    gap: theme.spacing(0.75),
    maxHeight: "46vh",
    overflowY: "auto",
    paddingRight: 4,
  },
  introRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
    position: "relative",
    zIndex: 2,
  },
  internalNoteBtn: {
    marginLeft: "auto",
    display: "inline-flex",
    alignItems: "center",
    gap: 4,
    cursor: "pointer",
    border: `1px solid ${theme.palette.divider}`,
    background: "transparent",
    color: theme.palette.text.secondary,
    fontSize: 12,
    fontWeight: 600,
    padding: "3px 10px",
    borderRadius: 14,
    whiteSpace: "nowrap",
    transition: "border-color 0.15s ease, color 0.15s ease",
    "&:hover": {
      borderColor: theme.palette.primary.main,
      color: theme.palette.primary.main,
    },
  },
  introText: {
    fontSize: 13,
    color: theme.palette.text.secondary,
  },
  cardsRow: {
    display: "flex",
    alignItems: "stretch",
    gap: theme.spacing(1),
    overflowX: "auto",
    overflowY: "hidden",
    paddingBottom: 6,
  },
  templateCard: {
    position: "relative",
    flex: "0 0 auto",
    width: 240,
    cursor: "pointer",
    borderRadius: 12,
    border: `1px solid ${theme.palette.mode === "light" ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.08)"}`,
    backgroundColor: theme.palette.mode === "light" ? "#fff" : "#233138",
    padding: theme.spacing(1.25),
    display: "flex",
    flexDirection: "column",
    gap: 6,
    transition: "border-color 0.15s ease, box-shadow 0.15s ease",
    "&:hover": {
      borderColor: theme.palette.primary.main,
    },
  },
  templateCardSelected: {
    borderColor: theme.palette.primary.main,
    boxShadow: `0 0 0 1px ${theme.palette.primary.main}`,
  },
  templateCardTop: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    gap: 6,
  },
  templateCardName: {
    fontWeight: 700,
    fontSize: 13,
    lineHeight: 1.25,
    overflow: "hidden",
    textOverflow: "ellipsis",
    whiteSpace: "nowrap",
  },
  templateBadgeRow: {
    display: "flex",
    flexWrap: "wrap",
    gap: 4,
  },
  templateBadge: {
    display: "inline-flex",
    alignItems: "center",
    gap: 3,
    fontSize: 10,
    fontWeight: 600,
    padding: "1px 6px",
    borderRadius: 6,
    backgroundColor: theme.palette.mode === "light" ? "#eef1f4" : "rgba(255,255,255,0.08)",
    color: theme.palette.text.secondary,
  },
  templateCheck: {
    color: theme.palette.primary.main,
    fontSize: 18,
    flexShrink: 0,
  },
  templateCardIcons: {
    display: "flex",
    alignItems: "center",
    gap: 4,
    flexShrink: 0,
  },
  templateMediaIcon: {
    fontSize: 16,
    color: theme.palette.text.secondary,
  },
  templateCardPreview: {
    fontSize: 11.5,
    lineHeight: 1.35,
    color: theme.palette.text.secondary,
    display: "-webkit-box",
    WebkitLineClamp: 2,
    WebkitBoxOrient: "vertical",
    overflow: "hidden",
    maxHeight: "3em",
    padding: "6px 8px",
    borderRadius: 8,
    borderTopLeftRadius: 2,
    backgroundColor: theme.palette.mode === "light" ? "#f2f4f6" : "rgba(255,255,255,0.04)",
  },
  previewWrapper: {
    width: "100%",
    display: "flex",
    flexDirection: "column",
    gap: 4,
  },
  previewLabel: {
    fontSize: 12,
    color: theme.palette.text.secondary,
  },
  previewCard: {
    width: "100%",
    borderRadius: 12,
    padding: theme.spacing(1, 1.5),
    background: theme.palette.mode === "light" ? "#fff" : "#233138",
    border: `1px solid ${theme.palette.mode === "light" ? "rgba(0,0,0,0.08)" : "rgba(255,255,255,0.08)"}`,
  },
  composeBar: {
    width: "100%",
    display: "flex",
    alignItems: "center",
    padding: "7px",
    borderRadius: 8,
    marginTop: 2,
    backgroundColor: theme.palette.mode === "light" ? "#eee" : "#171b20",
  },
  composeInputWrapper: {
    padding: 6,
    marginRight: 7,
    background: theme.palette.mode === "light" ? "#fff" : "#2a2f37",
    display: "flex",
    borderRadius: 20,
    flex: 1,
    minWidth: 0,
  },
  composeInput: {
    paddingLeft: 10,
    flex: 1,
    minWidth: 0,
    border: "none",
    color: theme.palette.text.primary,
    background: "transparent",
  },
  composeSendButton: {
    flexShrink: 0,
    color: "grey",
  },
  previewHeaderText: {
    fontWeight: 700,
    fontSize: 14,
    marginBottom: 4,
  },
  previewBody: {
    fontSize: 14,
    whiteSpace: "pre-wrap",
    wordBreak: "break-word",
  },
  previewVariablePlaceholder: {
    color: theme.palette.mode === "light" ? "#8a6d00" : "#ffd54f",
    fontWeight: 600,
  },
  previewVar: {
    cursor: "pointer",
    borderRadius: 4,
    padding: "0 3px",
  },
  previewVarEmpty: {
    color: theme.palette.mode === "light" ? "#8a6d00" : "#ffd54f",
    fontStyle: "italic",
  },
  previewVarFilled: {
    backgroundColor:
      theme.palette.mode === "light"
        ? "rgba(255,213,79,0.3)"
        : "rgba(255,213,79,0.16)",
  },
  previewVarActive: {
    outline: `1px dashed ${theme.palette.mode === "light" ? "#8a6d00" : "#ffd54f"}`,
  },
  previewFooter: {
    fontSize: 12,
    color: theme.palette.mode === "light" ? "rgba(0,0,0,0.5)" : "rgba(255,255,255,0.6)",
    marginTop: 4,
  },
  previewButtons: {
    display: "flex",
    flexDirection: "column",
    gap: 2,
    marginTop: 2,
    maxWidth: 320,
  },
  previewButton: {
    textAlign: "center",
    fontSize: 13,
    color: "#00a5f4",
    padding: "6px 8px",
    borderTop: `1px solid ${theme.palette.mode === "light" ? "rgba(0,0,0,0.1)" : "rgba(255,255,255,0.15)"}`,
    backgroundColor: theme.palette.mode === "light" ? "#fff" : "#233138",
  },
}));

const TemplateWindowPicker = ({ ticketId, whatsappId, contact, onWriteInternal, onExitTemplates }) => {
  const { classes, cx } = useStyles();
  const { list } = useTemplateMessage();
  const { user } = useContext(AuthContext);

  const [templates, setTemplates] = useState([]);
  const [loadingTemplates, setLoadingTemplates] = useState(true);
  const [selectedId, setSelectedId] = useState("");
  const [variableValues, setVariableValues] = useState([]);
  const [sending, setSending] = useState(false);
  const [headerAttachment, setHeaderAttachment] = useState(null);
  const [activeManualIdx, setActiveManualIdx] = useState(0);
  const attachmentInputRef = useRef(null);
  const composeInputRef = useRef(null);

  useEffect(() => {
    let mounted = true;
    (async () => {
      setLoadingTemplates(true);
      try {
        const companyId = localStorage.getItem("companyId");
        const data = await list({ companyId });
        if (!mounted) return;
        const filtered = (data || []).filter(
          (t) =>
            (String(t.whatsappId) === String(whatsappId) ||
              (t.whatsapps || []).some((w) => String(w.id) === String(whatsappId))) &&
            t.visibleToAgent !== false
        );
        setTemplates(filtered);
      } catch (err) {
        toastError(err);
      } finally {
        if (mounted) setLoadingTemplates(false);
      }
    })();
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [whatsappId]);

  const selectedTemplate = useMemo(
    () => templates.find((t) => String(t.id) === String(selectedId)),
    [templates, selectedId]
  );

  const sortedVariables = useMemo(
    () =>
      selectedTemplate?.variables
        ? [...selectedTemplate.variables].sort((a, b) => a.position - b.position)
        : [],
    [selectedTemplate]
  );

  const manualVars = useMemo(
    () =>
      sortedVariables
        .map((variable, idx) => ({ variable, idx }))
        .filter(({ variable }) => !AUTO_SOURCES.includes(variable.source)),
    [sortedVariables]
  );
  const activeManual = manualVars[activeManualIdx] || null;

  const missingVariables =
    sortedVariables.length > 0 &&
    variableValues.some((v) => !v || !v.trim());

  const needsHeaderMedia = MEDIA_HEADER_FORMATS.includes(
    selectedTemplate?.headerFormat
  );
  const hasStoredHeaderMedia = Boolean(selectedTemplate?.sampleMediaUrl);
  const requiresHeaderAttachment = needsHeaderMedia && !hasStoredHeaderMedia;
  const missingAttachment = requiresHeaderAttachment && !headerAttachment;

  const sendBlockedReason = useMemo(() => {
    if (!selectedTemplate) return "Elige una plantilla";
    if (sending) return "Enviando…";

    if (missingVariables) {
      const faltantes = sortedVariables
        .map((v, idx) => ({ v, idx }))
        .filter(({ idx }) => !variableValues[idx] || !variableValues[idx].trim())
        .map(({ v }) => v.label || `variable ${v.position}`);

      return faltantes.length === 1
        ? `Falta: ${faltantes[0]}`
        : `Faltan ${faltantes.length} variables`;
    }

    if (missingAttachment) {
      return `Adjunta ${HEADER_FORMAT_LABEL[selectedTemplate.headerFormat] || "el archivo"}`;
    }

    return null;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedTemplate, sending, missingVariables, missingAttachment, sortedVariables, variableValues]);

  const resolveAutoValue = (source) => {
    if (source === "CONTACT_NAME") return contact?.name || "";
    if (source === "USER_NAME") return user?.name || "";
    return "";
  };

  const handleVariableChange = (varIdx, value) => {
    setVariableValues((prev) => {
      const next = [...prev];
      next[varIdx] = value;
      return next;
    });
  };

  const previewBodyNodes = useMemo(() => {
    const text = selectedTemplate?.bodyText || "";
    if (!text) return null;

    const parts = text.split(/(\{\{\d+\}\})/g);
    return parts.map((part, i) => {
      const match = part.match(/^\{\{(\d+)\}\}$/);
      if (!match) return <React.Fragment key={i}>{part}</React.Fragment>;

      const position = Number(match[1]);
      const varIdx = sortedVariables.findIndex((v) => v.position === position);
      if (varIdx === -1) {
        return (
          <span key={i} className={classes.previewVariablePlaceholder}>
            {`{{${position}}}`}
          </span>
        );
      }

      const variable = sortedVariables[varIdx];
      const value = variableValues[varIdx] || "";

      if (AUTO_SOURCES.includes(variable.source)) {
        return <React.Fragment key={i}>{value || `{{${position}}}`}</React.Fragment>;
      }

      const manualPos = manualVars.findIndex((m) => m.idx === varIdx);
      const isActive = manualPos === activeManualIdx;
      const placeholderText = variable.label || `variable ${position}`;

      return (
        <span
          key={i}
          className={cx(
            classes.previewVar,
            value ? classes.previewVarFilled : classes.previewVarEmpty,
            { [classes.previewVarActive]: isActive }
          )}
          onClick={() => {
            if (manualPos !== -1) setActiveManualIdx(manualPos);
            composeInputRef.current?.focus();
          }}
        >
          {value || placeholderText}
        </span>
      );
    });
  }, [selectedTemplate, sortedVariables, variableValues, classes, cx, manualVars, activeManualIdx]);

  const renderInlineHeaderText = () => {
    if (selectedTemplate?.headerFormat === "TEXT" && selectedTemplate.headerText) {
      return (
        <div className={classes.previewHeaderText}>{selectedTemplate.headerText}</div>
      );
    }
    return null;
  };

  useEffect(() => {
    setVariableValues(
      sortedVariables.map((v) =>
        AUTO_SOURCES.includes(v.source) ? resolveAutoValue(v.source) : ""
      )
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sortedVariables, contact?.name, user?.name]);

  useEffect(() => {
    setHeaderAttachment(null);
    setActiveManualIdx(0);
  }, [selectedId]);

  const handleComposeKeyDown = (e) => {
    if (e.key !== "Enter") return;
    e.preventDefault();
    if (activeManualIdx < manualVars.length - 1) {
      setActiveManualIdx(activeManualIdx + 1);
      return;
    }
    if (!missingVariables && !missingAttachment && !sending) handleSend();
  };

  const composePlaceholder = activeManual
    ? `Escribe ${activeManual.variable.label || `la variable ${activeManual.variable.position}`}${
        activeManual.variable.exampleValue
          ? ` (ej: ${activeManual.variable.exampleValue})`
          : ""
      }...`
    : "Mensaje listo, presiona enviar";

  const handleAttachmentChange = (e) => {
    const file = e.target.files?.[0];
    if (file) setHeaderAttachment(file);
    e.target.value = "";
  };

  const handleSend = async () => {
    if (!selectedTemplate) return;
    setSending(true);
    try {
      let payload;
      if (headerAttachment) {
        payload = new FormData();
        payload.append("templateId", selectedTemplate.id);
        payload.append("variables", JSON.stringify(variableValues));
        payload.append("file", headerAttachment);
      } else {
        payload = {
          templateId: selectedTemplate.id,
          variables: variableValues,
        };
      }

      await api.post(`/messages/${ticketId}/send-template`, payload);
      toast.success(
        "Plantilla enviada. Espera la respuesta del cliente para continuar la conversación."
      );
      setSelectedId("");
      setVariableValues([]);
      setHeaderAttachment(null);
    } catch (err) {
      toastError(err);
    } finally {
      setSending(false);
    }
  };

  if (loadingTemplates) {
    return <Typography variant="body2">Cargando plantillas...</Typography>;
  }

  if (templates.length === 0) {
    return (
      <div className={classes.introRow}>
        <Tooltip title={WINDOW_EXPLANATION}>
          <Typography variant="body2" className={classes.introText}>
            No hay plantillas para esta conexión.
          </Typography>
        </Tooltip>
        {onWriteInternal && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<StickyNote2OutlinedIcon style={{ fontSize: 14 }} />}
            className={classes.internalNoteBtn}
            onClick={() => {
              console.log("[TemplateWindowPicker] Nota interna: clic recibido");
              onWriteInternal();
            }}
            title="Escribir una nota interna del equipo (no se envía al cliente)"
          >
            Nota interna
          </Button>
        )}
      </div>
    );
  }

  return (
    <div className={classes.wrapper}>
      <div className={classes.introRow}>
        <Tooltip title={WINDOW_EXPLANATION}>
          <InfoOutlinedIcon fontSize="small" color="disabled" />
        </Tooltip>

        {}
        {onExitTemplates && (
          <Tooltip title="Volver a escribir un mensaje normal">
            <IconButton size="small" onClick={onExitTemplates}>
              <DescriptionOutlinedIcon fontSize="small" color="primary" />
            </IconButton>
          </Tooltip>
        )}
        {onWriteInternal && (
          <Button
            size="small"
            variant="outlined"
            startIcon={<StickyNote2OutlinedIcon style={{ fontSize: 14 }} />}
            className={classes.internalNoteBtn}
            onClick={() => {
              console.log("[TemplateWindowPicker] Nota interna: clic recibido");
              onWriteInternal();
            }}
            title="Escribir una nota interna del equipo (no se envía al cliente)"
          >
            Nota interna
          </Button>
        )}
      </div>

      <div className={classes.cardsRow}>
        {templates.map((t) => {
          const isSelected = String(selectedId) === String(t.id);
          const needsMedia = MEDIA_HEADER_FORMATS.includes(t.headerFormat);
          const MediaIcon = MEDIA_TYPE_ICON[t.headerFormat];
          return (
            <div
              key={t.id}
              onClick={() => setSelectedId(t.id)}
              className={cx(classes.templateCard, {
                [classes.templateCardSelected]: isSelected,
              })}
            >
              <div className={classes.templateCardTop}>
                <Tooltip title={t.name}>
                  <Typography className={classes.templateCardName}>
                    {prettifyTemplateName(t.name)}
                  </Typography>
                </Tooltip>
                <span className={classes.templateCardIcons}>
                  {needsMedia && MediaIcon && (
                    <Tooltip title={MEDIA_TYPE_LABEL[t.headerFormat] || "Archivo"}>
                      <MediaIcon className={classes.templateMediaIcon} />
                    </Tooltip>
                  )}
                  {isSelected && <CheckCircleIcon className={classes.templateCheck} />}
                </span>
              </div>

              {t.bodyText && (
                <Typography className={classes.templateCardPreview}>
                  {t.bodyText}
                </Typography>
              )}
            </div>
          );
        })}
      </div>

      {selectedTemplate && (
        <div className={classes.previewWrapper}>
          <div className={classes.previewCard}>
            {renderInlineHeaderText()}
            <div className={classes.previewBody}>{previewBodyNodes}</div>
            {selectedTemplate.footerText && (
              <div className={classes.previewFooter}>{selectedTemplate.footerText}</div>
            )}
            {selectedTemplate.buttons?.length > 0 && (
              <div className={classes.previewButtons}>
                {[...selectedTemplate.buttons]
                  .sort((a, b) => a.position - b.position)
                  .map((btn) => (
                    <div key={btn.id || btn.position} className={classes.previewButton}>
                      {btn.text}
                    </div>
                  ))}
              </div>
            )}
          </div>

          <div className={classes.composeBar}>
            {requiresHeaderAttachment && (
              <>
                <Tooltip
                  title={
                    headerAttachment
                      ? headerAttachment.name
                      : `Adjuntar ${HEADER_FORMAT_LABEL[selectedTemplate.headerFormat]} (requerido)`
                  }
                >
                  <IconButton
                    size="large"
                    className={classes.composeSendButton}
                    onClick={() => attachmentInputRef.current?.click()}
                  >
                    <Badge color="error" variant="dot" invisible={!missingAttachment}>
                      <AttachFileIcon color={headerAttachment ? "primary" : "inherit"} />
                    </Badge>
                  </IconButton>
                </Tooltip>
                <input
                  type="file"
                  hidden
                  ref={attachmentInputRef}
                  accept={ACCEPT_BY_HEADER_FORMAT[selectedTemplate.headerFormat]}
                  onChange={handleAttachmentChange}
                />
              </>
            )}

            <div className={classes.composeInputWrapper}>
              <InputBase
                inputRef={composeInputRef}
                className={classes.composeInput}
                value={activeManual ? variableValues[activeManual.idx] || "" : ""}
                readOnly={!activeManual}
                placeholder={composePlaceholder}
                onChange={(e) =>
                  activeManual && handleVariableChange(activeManual.idx, e.target.value)
                }
                onKeyDown={handleComposeKeyDown}
              />
            </div>

            {}
            <Tooltip title={sendBlockedReason || "Enviar"}>
              <span>
                <IconButton
                  size="large"
                  color="primary"
                  className={classes.composeSendButton}
                  disabled={sending || missingVariables || missingAttachment}
                  onClick={handleSend}
                >
                  <SendIcon />
                </IconButton>
              </span>
            </Tooltip>
          </div>
        </div>
      )}
    </div>
  );
};

export default TemplateWindowPicker;
